/**
 * The landing page's icons.
 *
 * One family, drawn on a 24×24 grid at a 1.6 stroke, rounded caps and joins,
 * never filled. Mixing a filled set with a stroked one is the fastest way to
 * make a page look assembled from parts, and a page selling careful software
 * cannot afford to look assembled from parts.
 *
 * Inline rather than an icon package: there are fourteen of them, they are a
 * few hundred bytes each, and a dependency would ship several hundred that
 * nobody uses. Every one is `aria-hidden` — each sits beside a real heading
 * that already says what it means, so announcing it again would only make a
 * screen reader repeat itself.
 */

type IconProps = { className?: string };

function Icon({
  children,
  className,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      className={className ? `fc-icon ${className}` : "fc-icon"}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/* ── Promises ─────────────────────────────────────────────────────────────── */

export const LockIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="10.5" width="16" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </Icon>
);

export const KeyIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="12" r="3.5" />
    <path d="M11.5 12H20M17 12v3M20 12v2.5" />
  </Icon>
);

export const NoAdsIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m6 18 12-12" />
  </Icon>
);

export const ExportIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3v11M8.5 10.5 12 14l3.5-3.5" />
    <path d="M5 16v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3" />
  </Icon>
);

/* ── What you get ─────────────────────────────────────────────────────────── */

export const LibraryIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 15 4.5-4 4 3.5 3.5-3 6 5" />
    <circle cx="9" cy="9.5" r="1.4" />
  </Icon>
);

export const FacesIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="9" r="3.5" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 7.2a3.5 3.5 0 0 1 0 6.6M17.5 19a5.6 5.6 0 0 0-2-4.3" />
  </Icon>
);

export const MapPinIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 21s6.5-5.6 6.5-10.5a6.5 6.5 0 0 0-13 0C5.5 15.4 12 21 12 21z" />
    <circle cx="12" cy="10.5" r="2.4" />
  </Icon>
);

export const SlidersIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 6h14M5 12h14M5 18h14" />
    <circle cx="9" cy="6" r="2" />
    <circle cx="15" cy="12" r="2" />
    <circle cx="8" cy="18" r="2" />
  </Icon>
);

export const CropIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 2.5v15h15" />
    <path d="M2.5 6.5h15v15" />
  </Icon>
);

export const VideoIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="6" width="12.5" height="12" rx="2" />
    <path d="m15.5 10.5 5-2.5v8l-5-2.5z" />
  </Icon>
);

export const WallIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 14a2 2 0 0 1-2 2H8l-4 3.5V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    <path d="M8.5 9h7M8.5 12h4" />
  </Icon>
);

export const LinkIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 13.5a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7L11.5 6.3" />
    <path d="M14 10.5a4 4 0 0 0-5.7 0l-2.8 2.8a4 4 0 0 0 5.7 5.7l1.3-1.3" />
  </Icon>
);

export const ShieldIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3l7 3v5.5c0 4.4-3 8.2-7 9.5-4-1.3-7-5.1-7-9.5V6z" />
    <path d="m9 12 2 2 4-4" />
  </Icon>
);

export const PeopleIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <circle cx="17" cy="9.5" r="2.4" />
    <path d="M15 19a4.6 4.6 0 0 1 5.5-4.4" />
  </Icon>
);

export const CopyIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
  </Icon>
);

/* ── Marks used in the comparison ─────────────────────────────────────────── */

export const CheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);

export const CrossIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

export const PartialIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 12h12" />
  </Icon>
);

/* ── Platforms ────────────────────────────────────────────────────────────── */

/*
 * Drawn in the same family as everything else — 24×24, stroked, never filled.
 * A real Apple or Android logo would be a filled brand mark from somebody
 * else's guidelines sitting next to fourteen stroked outlines, which is exactly
 * the mismatch that makes a page look assembled from parts. These say phone,
 * robot, window and desk plainly enough.
 */

export const WebIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5a13 13 0 0 1 0 17 13 13 0 0 1 0-17z" />
  </Icon>
);

export const PhoneIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.2" />
    <path d="M10.5 5.5h3" />
    <path d="M11 18.5h2" />
  </Icon>
);

export const AndroidIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 11.5h14v6a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 17.5z" />
    <path d="M7.5 11.5a4.5 4.5 0 0 1 9 0" />
    <path d="m6.6 6.2 1.2 2M17.4 6.2l-1.2 2" />
    <path d="M3 12.5v3.5M21 12.5v3.5" />
  </Icon>
);

export const DesktopIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="4" width="19" height="12.5" rx="1.6" />
    <path d="M8 20h8M12 16.5V20" />
  </Icon>
);
